/**
 * AI Support System Types
 * Types for OpenAI Realtime API integration with Twilio
 */

// =============================================================================
// AI AGENT TYPES
// =============================================================================

export type VoiceProvider = 'elevenlabs' | 'openai';
export type OpenAIVoice = 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';
export type FallbackBehavior = 'voicemail' | 'forward' | 'hangup';

export interface AvailableHours {
  monday?: { start: string; end: string };
  tuesday?: { start: string; end: string };
  wednesday?: { start: string; end: string };
  thursday?: { start: string; end: string };
  friday?: { start: string; end: string };
  saturday?: { start: string; end: string };
  sunday?: { start: string; end: string };
}

export interface AIAgent {
  id: string;
  name: string;
  description?: string;

  // OpenAI Configuration
  openaiApiKeyEncrypted?: string;
  openaiAssistantId?: string;
  openaiPromptId?: string; // Stored prompt ID from OpenAI Platform
  openaiModel: string;
  systemPrompt: string;

  // Voice Configuration
  voiceProvider: VoiceProvider;
  elevenlabsVoiceId?: string;
  openaiVoice: OpenAIVoice;

  // Twilio Integration
  twilioPhoneNumber?: string;
  twilioPhoneSid?: string;
  twilioProvisionedAt?: string;

  // Business Logic
  available247: boolean;
  availableHours: AvailableHours;
  fallbackBehavior: FallbackBehavior;
  fallbackForwardTo?: string;

  // AI Capabilities
  enabledFunctions: string[];
  maxConversationDurationSeconds: number;
  temperature: number;
  maxResponseTokens: number;

  // Features
  conversationRecordingEnabled: boolean;
  transcriptStorageEnabled: boolean;
  aiSummaryEnabled: boolean;
  sentimentAnalysisEnabled: boolean;

  // Status
  isActive: boolean;
  lastCallAt?: string;
  totalCallsHandled: number;

  // Ownership
  organizationId: string;
  createdBy?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Database format (snake_case)
export interface AIAgentDB {
  id: string;
  name: string;
  description?: string;
  openai_api_key_encrypted?: string;
  openai_assistant_id?: string;
  openai_prompt_id?: string;
  openai_model: string;
  system_prompt: string;
  voice_provider: VoiceProvider;
  elevenlabs_voice_id?: string;
  openai_voice: OpenAIVoice;
  twilio_phone_number?: string;
  twilio_phone_sid?: string;
  twilio_provisioned_at?: string;
  available_24_7: boolean;
  available_hours: AvailableHours;
  fallback_behavior: FallbackBehavior;
  fallback_forward_to?: string;
  enabled_functions: string[];
  max_conversation_duration_seconds: number;
  temperature: number;
  max_response_tokens: number;
  conversation_recording_enabled: boolean;
  transcript_storage_enabled: boolean;
  ai_summary_enabled: boolean;
  sentiment_analysis_enabled: boolean;
  is_active: boolean;
  last_call_at?: string;
  total_calls_handled: number;
  organization_id: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// AI CONVERSATION TYPES
// =============================================================================

export type CallStatus =
  | 'queued'
  | 'ringing'
  | 'in-progress'
  | 'completed'
  | 'failed'
  | 'busy'
  | 'no-answer'
  | 'canceled';

export type Sentiment = 'positive' | 'neutral' | 'negative';
export type FollowupPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface TranscriptMessage {
  role: 'user' | 'assistant' | 'function' | 'system';
  content: string;
  timestamp: number; // milliseconds from call start
  audioUrl?: string;
}

export interface AIConversation {
  id: string;

  // Relationships
  agentId: string;
  contactId?: string;

  // Caller Information
  callerPhone: string;
  callerName?: string;
  callerEmail?: string;

  // Call Metadata
  twilioCallSid: string;
  callStartedAt: string;
  callEndedAt?: string;
  callDurationSeconds?: number;
  callStatus: CallStatus;

  // Conversation Analysis
  fullTranscript: TranscriptMessage[];
  summary?: string;
  intent?: string;
  sentiment?: Sentiment;
  outcome?: string;
  keyPoints: string[];

  // Follow-up Management
  requiresFollowup: boolean;
  followupPriority?: FollowupPriority;
  followupNotes?: string;
  followupDueDate?: string;
  assignedTo?: string;
  followupCompletedAt?: string;

  // Recording & Storage
  recordingUrl?: string;
  recordingDurationSeconds?: number;
  recordingStoragePath?: string;
  recordingTranscriptionStatus: TranscriptionStatus;

  // AI Performance Metrics
  openaiSessionId?: string;
  totalTokensUsed: number;
  inputTokens: number;
  outputTokens: number;
  costEstimateCents: number;

  // Function Calls Summary
  functionsCalled: string[];
  functionsSucceeded: number;
  functionsFailed: number;

  // Quality Metrics
  audioQualityScore?: number;
  silenceDurationSeconds?: number;
  interruptionsCount: number;

  // Metadata
  metadata: Record<string, any>;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Database format (snake_case)
export interface AIConversationDB {
  id: string;
  agent_id: string;
  contact_id?: string;
  caller_phone: string;
  caller_name?: string;
  caller_email?: string;
  twilio_call_sid: string;
  call_started_at: string;
  call_ended_at?: string;
  call_duration_seconds?: number;
  call_status: CallStatus;
  full_transcript: TranscriptMessage[];
  summary?: string;
  intent?: string;
  sentiment?: Sentiment;
  outcome?: string;
  key_points: string[];
  requires_followup: boolean;
  followup_priority?: FollowupPriority;
  followup_notes?: string;
  followup_due_date?: string;
  assigned_to?: string;
  followup_completed_at?: string;
  recording_url?: string;
  recording_duration_seconds?: number;
  recording_storage_path?: string;
  recording_transcription_status: TranscriptionStatus;
  openai_session_id?: string;
  total_tokens_used: number;
  input_tokens: number;
  output_tokens: number;
  cost_estimate_cents: number;
  functions_called: string[];
  functions_succeeded: number;
  functions_failed: number;
  audio_quality_score?: number;
  silence_duration_seconds?: number;
  interruptions_count: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// AI MESSAGE TYPES
// =============================================================================

export type MessageRole = 'user' | 'assistant' | 'function' | 'system';

export interface AIMessage {
  id: string;
  conversationId: string;

  // Message Content
  role: MessageRole;
  content: string;

  // Audio Data
  audioTranscript?: string;
  audioUrl?: string;
  audioDurationMs?: number;
  audioFormat?: string;

  // Function Call Data
  functionName?: string;
  functionArguments?: Record<string, any>;
  functionResult?: Record<string, any>;
  functionCallId?: string;

  // Timing
  timestampOffsetMs?: number;
  turnNumber?: number;

  // Quality Metrics
  confidenceScore?: number;
  detectedLanguage: string;

  // Metadata
  rawEventData?: Record<string, any>;

  createdAt: string;
}

// Database format (snake_case)
export interface AIMessageDB {
  id: string;
  conversation_id: string;
  role: MessageRole;
  content: string;
  audio_transcript?: string;
  audio_url?: string;
  audio_duration_ms?: number;
  audio_format?: string;
  function_name?: string;
  function_arguments?: Record<string, any>;
  function_result?: Record<string, any>;
  function_call_id?: string;
  timestamp_offset_ms?: number;
  turn_number?: number;
  confidence_score?: number;
  detected_language: string;
  raw_event_data?: Record<string, any>;
  created_at: string;
}

// =============================================================================
// AI FUNCTION CALL TYPES
// =============================================================================

export interface AIFunctionCall {
  id: string;
  conversationId: string;
  messageId?: string;

  // Function Details
  functionName: string;
  functionCallId?: string;
  functionArgs: Record<string, any>;
  functionResult?: Record<string, any>;

  // Execution
  executedAt: string;
  executionDurationMs?: number;
  success?: boolean;
  errorMessage?: string;
  errorCode?: string;

  // Context
  triggeredBy?: string;
  retryCount: number;

  // Results Impact
  resultSentToAi: boolean;
  aiFollowupAction?: string;

  createdAt: string;
}

// Database format (snake_case)
export interface AIFunctionCallDB {
  id: string;
  conversation_id: string;
  message_id?: string;
  function_name: string;
  function_call_id?: string;
  function_args: Record<string, any>;
  function_result?: Record<string, any>;
  executed_at: string;
  execution_duration_ms?: number;
  success?: boolean;
  error_message?: string;
  error_code?: string;
  triggered_by?: string;
  retry_count: number;
  result_sent_to_ai: boolean;
  ai_followup_action?: string;
  created_at: string;
}

// =============================================================================
// AI ANALYTICS TYPES
// =============================================================================

export interface AIAgentStats {
  totalCalls: number;
  completedCalls: number;
  failedCalls: number;
  avgDurationSeconds: number;
  totalFollowupsCreated: number;
  followupsPending: number;
  totalCostCents: number;
  mostCommonIntent?: string;
}

export interface AICallMetrics {
  callsPerDay: { date: string; count: number }[];
  callsByStatus: { status: CallStatus; count: number }[];
  callsByIntent: { intent: string; count: number }[];
  avgDurationByDay: { date: string; avgSeconds: number }[];
  costByDay: { date: string; costCents: number }[];
}

// =============================================================================
// CONVERSATION SUMMARY VIEW
// =============================================================================

export interface AIConversationSummary {
  id: string;
  agentId: string;
  contactId?: string;
  callerPhone: string;
  callerName?: string;
  callerEmail?: string;
  callStartedAt: string;
  callEndedAt?: string;
  callDurationSeconds?: number;
  callStatus: CallStatus;
  summary?: string;
  intent?: string;
  sentiment?: Sentiment;
  requiresFollowup: boolean;
  followupPriority?: FollowupPriority;
  assignedTo?: string;
  followupCompletedAt?: string;
  costEstimateCents: number;

  // Agent info
  agentName: string;
  organizationId: string;

  // Assigned user info
  assignedToFirstName?: string;
  assignedToLastName?: string;
  assignedToEmail?: string;

  // Contact info
  contactName?: string;
  contactEmail?: string;

  // Counts
  messageCount: number;
  functionCallsCount: number;

  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// TRANSFORMATION FUNCTIONS
// =============================================================================

export function transformAIAgentFromDB(dbAgent: AIAgentDB): AIAgent {
  return {
    id: dbAgent.id,
    name: dbAgent.name,
    description: dbAgent.description,
    openaiApiKeyEncrypted: dbAgent.openai_api_key_encrypted,
    openaiAssistantId: dbAgent.openai_assistant_id,
    openaiPromptId: dbAgent.openai_prompt_id,
    openaiModel: dbAgent.openai_model,
    systemPrompt: dbAgent.system_prompt,
    voiceProvider: dbAgent.voice_provider,
    elevenlabsVoiceId: dbAgent.elevenlabs_voice_id,
    openaiVoice: dbAgent.openai_voice,
    twilioPhoneNumber: dbAgent.twilio_phone_number,
    twilioPhoneSid: dbAgent.twilio_phone_sid,
    twilioProvisionedAt: dbAgent.twilio_provisioned_at,
    available247: dbAgent.available_24_7,
    availableHours: dbAgent.available_hours,
    fallbackBehavior: dbAgent.fallback_behavior,
    fallbackForwardTo: dbAgent.fallback_forward_to,
    enabledFunctions: dbAgent.enabled_functions,
    maxConversationDurationSeconds: dbAgent.max_conversation_duration_seconds,
    temperature: dbAgent.temperature,
    maxResponseTokens: dbAgent.max_response_tokens,
    conversationRecordingEnabled: dbAgent.conversation_recording_enabled,
    transcriptStorageEnabled: dbAgent.transcript_storage_enabled,
    aiSummaryEnabled: dbAgent.ai_summary_enabled,
    sentimentAnalysisEnabled: dbAgent.sentiment_analysis_enabled,
    isActive: dbAgent.is_active,
    lastCallAt: dbAgent.last_call_at,
    totalCallsHandled: dbAgent.total_calls_handled,
    organizationId: dbAgent.organization_id,
    createdBy: dbAgent.created_by,
    createdAt: dbAgent.created_at,
    updatedAt: dbAgent.updated_at
  };
}

export function transformAIConversationFromDB(dbConv: AIConversationDB): AIConversation {
  return {
    id: dbConv.id,
    agentId: dbConv.agent_id,
    contactId: dbConv.contact_id,
    callerPhone: dbConv.caller_phone,
    callerName: dbConv.caller_name,
    callerEmail: dbConv.caller_email,
    twilioCallSid: dbConv.twilio_call_sid,
    callStartedAt: dbConv.call_started_at,
    callEndedAt: dbConv.call_ended_at,
    callDurationSeconds: dbConv.call_duration_seconds,
    callStatus: dbConv.call_status,
    fullTranscript: dbConv.full_transcript,
    summary: dbConv.summary,
    intent: dbConv.intent,
    sentiment: dbConv.sentiment,
    outcome: dbConv.outcome,
    keyPoints: dbConv.key_points,
    requiresFollowup: dbConv.requires_followup,
    followupPriority: dbConv.followup_priority,
    followupNotes: dbConv.followup_notes,
    followupDueDate: dbConv.followup_due_date,
    assignedTo: dbConv.assigned_to,
    followupCompletedAt: dbConv.followup_completed_at,
    recordingUrl: dbConv.recording_url,
    recordingDurationSeconds: dbConv.recording_duration_seconds,
    recordingStoragePath: dbConv.recording_storage_path,
    recordingTranscriptionStatus: dbConv.recording_transcription_status,
    openaiSessionId: dbConv.openai_session_id,
    totalTokensUsed: dbConv.total_tokens_used,
    inputTokens: dbConv.input_tokens,
    outputTokens: dbConv.output_tokens,
    costEstimateCents: dbConv.cost_estimate_cents,
    functionsCalled: dbConv.functions_called,
    functionsSucceeded: dbConv.functions_succeeded,
    functionsFailed: dbConv.functions_failed,
    audioQualityScore: dbConv.audio_quality_score,
    silenceDurationSeconds: dbConv.silence_duration_seconds,
    interruptionsCount: dbConv.interruptions_count,
    metadata: dbConv.metadata,
    createdAt: dbConv.created_at,
    updatedAt: dbConv.updated_at
  };
}
